/* -------------------------- Design imports start -------------------------- */
import { TextField, Grid, Typography, Autocomplete } from '@mui/material'
/* --------------------------- Design imports end --------------------------- */

/* ------------------------ Functional imports start ------------------------ */
import React, { type ReactElement, useEffect, useState } from "react";
import { useTranslation } from 'react-i18next'
import { toast } from 'react-toastify'

import Button from '../../../../components/Button'
import LogTool from '../../../../logger/logTools'
import { useUserContext } from '../../../../utils/context'
import { type User } from "../../../../utils/types";
import { useChatContext } from '../../utils/context'
import { type Channel } from '../../utils/types'
import { useUsers } from '../../../employee/hooks/useEmployeeQueries'
import { useCreateChannel, useAddMemberToChannel } from '../../hooks/useChatMutations'
/* ------------------------- Functional imports end ------------------------- */
/**
 * Page that lets you create a new channel
 *  - Reference
 *  - Product
 *  - To: (Receiver (only for admin))
 */
/* -------------------------------------------------------------------------- */
/*                               Component start                              */
/* -------------------------------------------------------------------------- */
export default function NewChannel(): ReactElement {
  /* -------------------------- Non state data start -------------------------- */
  const {
    //activeUser,
    fetchChannels,
    setChannels,
    activeChannel,
    setActiveChannel,
  } = useChatContext()
  const log = new LogTool({ context: 'NewChannel', enable: true, logLevel: 'warn' })
  /* --------------------------- Non state data end --------------------------- */
  /* ---------------------------- React Query Hooks --------------------------- */
  const { data: usersData } = useUsers({ 
    expand: ['roles__sub_roles'],
    filters: { exclude: 'roles__self' }
  })
  const createChannelMutation = useCreateChannel()
  const addMemberMutation = useAddMemberToChannel()
  /* ---------------------------- React Query Hooks End ----------------------- */
  /* ---------------------------- Flag states start --------------------------- */
  /* ----------------------------- Flag states end ---------------------------- */
  /* ---------------------------- Data states start --------------------------- */
  const { t } = useTranslation(['chat', 'common'])
  const { user } = useUserContext()
  const users = usersData?.results || []
  const [subject, setSubject] = useState<string>('')
  const [members, setMembers] = useState<User[]>([])
  /* ----------------------------- Data states end ---------------------------- */

  /* ------------------------------ Effects start ----------------------------- */
  log.info('NewChannel rendered')
  /* ------------------------------- Effects end ------------------------------ */

  /* ------------------------- Utility functions start ------------------------ */
  function userAlreadyInChannel(channel: Channel | undefined, userInChannel: User): boolean {
    if (!channel && user) {
      return userInChannel.self === user.self
    }
    if (!channel || !channel.members || typeof channel.members === "string") return false
    const channelUsers = channel.members.map((user: User) => user.self)
    return channelUsers.includes(userInChannel.self)
  }

  const createNewChat = async (): Promise<void> => {
    try {
      const newChannel = await createChannelMutation.mutateAsync({
        subject: subject,
      })
      
      const result: boolean = await addMembers(newChannel)
      if (result) {
        toast.success(t('common:feedback.success.chatCreatedSuccessfully'))
      } else {
        toast.error(t('common:feedback.error.chatCreatedButMembersFailed'))
      }
      log.info('Chat created', newChannel)
      
      setSubject('')
      setMembers([])
      
      await fetchChannels({
        onSuccess: (channels: Channel[]) => {
          handleChannelsUpdate(channels, newChannel)
        },
        parameter: ['expand=members,creator,messages'],
      })
    } catch (error) {
      log.error('Error creating chat', error)
      toast.error(t('common:feedback.error.chatCreatedError'))
    }
  }

  const addMembers = async (channel: Channel): Promise<boolean> => {
    const results: boolean[] = []
    if (!channel || !channel.members || typeof channel.members !== "string") return false
    
    try {
      await Promise.all(members.map(async (member: User): Promise<void> => {
        try {
          await addMemberMutation.mutateAsync({
            membersUrl: channel.members as string,
            userSelf: member.self,
          })
          results.push(true)
        } catch (error) {
          log.error('Error adding member', error)
          results.push(false)
        }
      }))
    } catch (error) {
      log.error('Error in addMembers', error)
      return false
    }
    
    return !results.includes(false)
  }

  /* -------------------------- Utility functions end ------------------------- */
  /* ------------------------ Helper functions start ------------------------ */
  function handleChannelsUpdate(channels: Channel[], response: any): void {
    let newChannel: Channel | undefined = channels.find(
      (channel: Channel) => channel.self === response.self
    )
    if (newChannel && typeof newChannel.members !== 'string') {
      newChannel = { ...newChannel, members: [...newChannel.members, ...members] }
    }
    const newChannels: (Channel | undefined)[] = channels.map((channel: Channel) => {
      if (channel.self === response.self) {
        return newChannel
      } else {
        return channel
      }
    })
    setChannels(newChannels as Channel[])
    setActiveChannel(newChannel as Channel)
  }

  /* ------------------------- Helper functions end ------------------------- */

  /* -------------------------------------------------------------------------- */
  /*                              Render component                              */
  /* -------------------------------------------------------------------------- */
  return (
    <Grid container spacing={2}>
      <Grid item xs={12}>
        <Typography variant="h5">{t('content.label.newChat')}</Typography>
      </Grid>
      <Grid item xs={12}>
        <TextField
          id="channel-subject"
          label={t('content.label.subject')}
          variant="outlined"
          fullWidth
          value={subject}
          onChange={event => setSubject(event.target.value)}
        />
      </Grid>
      <Grid item xs={12}>
        <Autocomplete
          multiple
          id="tags-outlined"
          options={users.filter((user: User) => !userAlreadyInChannel(activeChannel, user))}
          getOptionLabel={(user: User) =>
            (user.firstName && user.lastName
              ? `${user.firstName} ${user.lastName}`
              : user.email) ?? ''
          }
          filterSelectedOptions
          onChange={(event, value) => {
            setMembers(value)
          }}
          renderInput={params => <TextField {...params} label={t('common:content.label.users')} />}
        />
      </Grid>
      <Grid item xs={12}>
        <Button
          id="createNewChatButton"
          variant="contained"
          onClick={createNewChat}
          sx={{ float: 'right' }}
        >
          {t('interaction.button.createNewChat')}
        </Button>
      </Grid>
    </Grid>
  )
}
